import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/store/auth-store';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'Mínimo 6 caracteres'),
});

type LoginInput = z.infer<typeof loginSchema>;

export function LoginPage() {
  const signIn = useAuthStore((state) => state.signInWithPassword);
  const navigate = useNavigate();
  const [authError, setAuthError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (input: LoginInput) => {
    setAuthError(null);
    const { error } = await signIn(input.email, input.password);
    if (error) {
      setAuthError(error.message);
      return;
    }
    navigate('/dashboard', { replace: true });
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-8">
      <Card className="w-full max-w-md border-border/60">
        <CardHeader>
          <CardTitle
            className="font-brand text-2xl tracking-[0.3em] text-foreground"
            data-testid="login-title"
          >
            TENNIS SCANNER
          </CardTitle>
          <p className="text-sm text-muted-foreground">Sign in con tu cuenta de operador.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
            <div className="flex flex-col gap-1">
              <label htmlFor="email" className="text-sm text-muted-foreground">
                Email
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                data-testid="email-input"
                {...register('email')}
              />
              {errors.email ? (
                <span className="text-xs text-destructive" data-testid="email-error">
                  {errors.email.message}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="password" className="text-sm text-muted-foreground">
                Password
              </label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                data-testid="password-input"
                {...register('password')}
              />
              {errors.password ? (
                <span className="text-xs text-destructive" data-testid="password-error">
                  {errors.password.message}
                </span>
              ) : null}
            </div>
            {authError ? (
              <div
                role="alert"
                className="rounded border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive"
                data-testid="auth-error"
              >
                {authError}
              </div>
            ) : null}
            <Button type="submit" disabled={isSubmitting} data-testid="login-submit">
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              No tenés cuenta?{' '}
              <Link to="/signup" className="text-primary underline" data-testid="link-signup">
                Crear cuenta
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
